import * as XLSX from "xlsx";

/**
 * Export data to Excel file
 * @param {Object} options - Export options
 * @param {Array} options.data - Array of objects to export
 * @param {string} options.filename - Name of the file
 * @param {string} options.sheetName - Name of the sheet
 * @param {Object} options.columns - Column mapping { key: label }
 * @param {string} options.language - 'ar' or 'en'
 */
export const exportToExcel = ({ data, filename, sheetName = "Data", columns, language = "en" }) => {
  if (!data || data.length === 0) {
    throw new Error(language === "ar" ? "لا توجد بيانات للتصدير" : "No data to export");
  }

  // Transform data based on column mapping
  const transformedData = data.map(item => {
    const row = {};
    Object.entries(columns).forEach(([key, label]) => {
      row[label] = item[key] ?? "";
    });
    return row;
  });

  // Create workbook and worksheet
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(transformedData);
  
  // Set column widths
  const colWidths = Object.values(columns).map(label => ({ wch: Math.max(label.length + 5, 15) }));
  ws["!cols"] = colWidths;

  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

/**
 * Export inventory data to Excel
 */
export const exportInventoryToExcel = (products, language = "en") => {
  const columns = language === "ar" 
    ? {
        name: "اسم المنتج",
        barcode: "الباركود",
        quantity: "الكمية",
        purchase_price: "سعر الشراء",
        sale_price: "سعر البيع",
        category: "الفئة",
        expiry_date: "تاريخ الانتهاء",
        production_date: "تاريخ الإنتاج"
      }
    : {
        name: "Product Name",
        barcode: "Barcode",
        quantity: "Quantity",
        purchase_price: "Purchase Price",
        sale_price: "Sale Price",
        category: "Category",
        expiry_date: "Expiry Date",
        production_date: "Production Date"
      };

  exportToExcel({
    data: products,
    filename: language === "ar" ? "المخزون" : "Inventory",
    sheetName: language === "ar" ? "المنتجات" : "Products",
    columns,
    language
  });
};

/**
 * Export customers data to Excel
 */
export const exportCustomersToExcel = (customers, language = "en") => {
  const columns = language === "ar" 
    ? {
        name: "اسم العميل",
        phone: "رقم الهاتف",
        address: "العنوان",
        debt: "الدين",
        loyalty_points: "نقاط الولاء"
      }
    : {
        name: "Customer Name",
        phone: "Phone",
        address: "Address",
        debt: "Debt",
        loyalty_points: "Loyalty Points"
      };

  exportToExcel({
    data: customers,
    filename: language === "ar" ? "العملاء" : "Customers",
    sheetName: language === "ar" ? "قائمة العملاء" : "Customer List",
    columns,
    language
  });
};

/**
 * Export sales data to Excel
 */
export const exportSalesToExcel = (sales, language = "en") => {
  const columns = language === "ar" 
    ? {
        created_at: "التاريخ",
        customer_name: "العميل",
        total: "المبلغ",
        payment_type: "نوع الدفع",
        earned_points: "النقاط المكتسبة"
      }
    : {
        created_at: "Date",
        customer_name: "Customer",
        total: "Amount",
        payment_type: "Payment Type",
        earned_points: "Points Earned"
      };

  exportToExcel({
    data: sales,
    filename: language === "ar" ? "المبيعات" : "Sales",
    sheetName: language === "ar" ? "سجل المبيعات" : "Sales Record",
    columns,
    language
  });
};
